import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { CheckCircle2, XCircle, Clock, ArrowRight, Sparkles, Lock } from "lucide-react";

const DEMO_QUESTIONS = [
  {
    category: "Temario · Constitución",
    text: "¿En qué fecha fue ratificada la Constitución Española por referéndum?",
    options: [
      "6 de diciembre de 1977",
      "6 de diciembre de 1978",
      "27 de diciembre de 1978",
      "29 de diciembre de 1978",
    ],
    correct: 1,
    explanation: "La CE fue ratificada por referéndum el 6 de diciembre de 1978. Se sancionó el 27 de diciembre y entró en vigor el 29.",
  },
  {
    category: "Ortografía · Acentuación",
    text: "¿Qué palabra está correctamente acentuada?",
    options: ["Exámen", "Examen", "Éxamen", "Exámenes"],
    correct: 1,
    explanation: "«Examen» es una palabra llana acabada en -n pero el plural «exámenes» sí lleva tilde por convertirse en esdrújula.",
  },
  {
    category: "Psicotécnicos · Series",
    text: "Continúa la serie: 2, 4, 8, 16, ?",
    options: ["24", "30", "32", "20"],
    correct: 2,
    explanation: "Cada término se multiplica por 2: 2×2=4, 4×2=8, 8×2=16, 16×2=32.",
  },
];

export default function LiveDemo() {
  const [idx, setIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [progress, setProgress] = useState([]);  // array of true/false
  const [timer, setTimer] = useState(45);
  const timerRef = useRef(null);

  const q = DEMO_QUESTIONS[idx];
  const finished = idx >= DEMO_QUESTIONS.length;

  /* Timer countdown */
  useEffect(() => {
    if (revealed || finished) return;
    setTimer(45);
    timerRef.current = setInterval(() => {
      setTimer((t) => {
        if (t <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [idx, revealed, finished]);

  const choose = (i) => {
    if (revealed) return;
    clearInterval(timerRef.current);
    setSelected(i);
    setRevealed(true);
    setProgress((p) => [...p, i === q.correct]);
  };

  const next = () => {
    setSelected(null);
    setRevealed(false);
    setIdx((i) => i + 1);
  };

  const restart = () => {
    setIdx(0);
    setSelected(null);
    setRevealed(false);
    setProgress([]);
  };

  const correctCount = progress.filter(Boolean).length;

  return (
    <section className="relative py-32 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto" data-testid="live-demo-section">
      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-2">
          <span className="h-px w-8 bg-gcgold/50" />
          <span className="text-[10px] font-mono uppercase tracking-[0.28em] text-gcgold">Pruébalo ya</span>
        </div>
        <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-[-0.03em] text-white leading-[0.95]">
          Mini-test{" "}
          <span
            className="font-serif italic text-gcgold"
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              textShadow: "0 0 30px rgba(212,175,55,0.5)",
            }}
          >
            en vivo
          </span>
          .
        </h2>
        <p className="mt-4 text-base sm:text-lg text-white/60 max-w-xl mx-auto">
          Sin registro. Tres preguntas reales del examen, con corrección y explicación. Justo como en tu primer simulacro.
        </p>
      </div>

      {/* "Macbook" frame */}
      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.95 }}
        whileInView={{ opacity: 1, y: 0, scale: 1 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="relative max-w-4xl mx-auto"
      >
        <div className="relative rounded-3xl border border-white/10 bg-gradient-to-b from-ink-800 to-ink overflow-hidden shadow-[0_30px_120px_-20px_rgba(0,0,0,0.7),0_0_60px_-15px_rgba(212,175,55,0.15)]">
          {/* Browser chrome */}
          <div className="flex items-center gap-2 px-4 py-3 border-b border-white/5 bg-ink-900/60">
            <span className="h-3 w-3 rounded-full bg-rose-500/70" />
            <span className="h-3 w-3 rounded-full bg-amber-400/70" />
            <span className="h-3 w-3 rounded-full bg-emerald-500/70" />
            <div className="flex-1 mx-4 px-3 py-1 rounded-md bg-white/[0.04] text-[11px] font-mono text-white/40 text-center truncate">
              testopoguardiacivil.com / simulacro
            </div>
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-gcgold flex items-center gap-1.5">
              <Sparkles className="h-3 w-3" /> Demo
            </div>
          </div>

          {/* Test UI */}
          <div className="p-6 sm:p-10 min-h-[480px]">
            {!finished ? (
              <AnimatePresence mode="wait">
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: 24 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -24 }}
                  transition={{ duration: 0.35 }}
                >
                  {/* Top bar */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-gcgold">{q.category}</div>
                    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/5 border border-white/5">
                      <Clock className="h-3 w-3 text-white/50" />
                      <span className="text-[11px] font-mono tabular-nums font-bold text-white/80">
                        00:{String(timer).padStart(2, "0")}
                      </span>
                    </div>
                  </div>

                  {/* Progress dots */}
                  <div className="flex items-center gap-1.5 mb-7">
                    {DEMO_QUESTIONS.map((_, i) => {
                      const isCurrent = i === idx;
                      const wasAnswered = i < idx;
                      const wasCorrect = wasAnswered && progress[i];
                      return (
                        <div
                          key={i}
                          className={`h-1.5 flex-1 rounded-full transition-colors ${
                            isCurrent ? "bg-gcgold" :
                            wasAnswered ? (wasCorrect ? "bg-emerald-500/70" : "bg-rose-500/70") :
                            "bg-white/[0.06]"
                          }`}
                        />
                      );
                    })}
                  </div>

                  {/* Question */}
                  <h3 className="font-display text-xl sm:text-2xl font-bold text-white leading-snug tracking-tight mb-7">
                    {q.text}
                  </h3>

                  {/* Options */}
                  <div className="space-y-2.5">
                    {q.options.map((opt, i) => {
                      const isSelected = selected === i;
                      const isCorrect = i === q.correct;
                      let state = "default";
                      if (revealed) {
                        if (isCorrect) state = "correct";
                        else if (isSelected) state = "wrong";
                        else state = "muted";
                      }
                      return (
                        <motion.button
                          key={i}
                          whileHover={!revealed ? { x: 4 } : {}}
                          whileTap={!revealed ? { scale: 0.99 } : {}}
                          onClick={() => choose(i)}
                          disabled={revealed}
                          data-testid={`demo-opt-${i}`}
                          className={`w-full text-left px-4 py-3.5 rounded-xl border transition-all flex items-center gap-3 ${
                            state === "correct" ? "bg-emerald-500/15 border-emerald-500/50 text-white" :
                            state === "wrong" ? "bg-rose-500/15 border-rose-500/50 text-white" :
                            state === "muted" ? "bg-white/[0.02] border-white/[0.04] text-white/40" :
                            "bg-white/[0.03] border-white/[0.06] text-white/85 hover:bg-white/[0.06] hover:border-gcgold/30"
                          }`}
                        >
                          <span className={`h-7 w-7 rounded-md flex items-center justify-center text-xs font-bold font-mono flex-shrink-0 ${
                            state === "correct" ? "bg-emerald-500 text-ink" :
                            state === "wrong" ? "bg-rose-500 text-white" :
                            "bg-white/5 text-white/60"
                          }`}>
                            {String.fromCharCode(65 + i)}
                          </span>
                          <span className="flex-1 text-sm sm:text-base">{opt}</span>
                          {state === "correct" && <CheckCircle2 className="h-5 w-5 text-emerald-400 flex-shrink-0" />}
                          {state === "wrong" && <XCircle className="h-5 w-5 text-rose-400 flex-shrink-0" />}
                        </motion.button>
                      );
                    })}
                  </div>

                  {/* Explanation */}
                  <AnimatePresence>
                    {revealed && (
                      <motion.div
                        initial={{ opacity: 0, height: 0, marginTop: 0 }}
                        animate={{ opacity: 1, height: "auto", marginTop: 24 }}
                        exit={{ opacity: 0, height: 0, marginTop: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="rounded-xl border border-gcgold/20 bg-gcgold/[0.04] p-4">
                          <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.22em] text-gcgold mb-2">
                            <Sparkles className="h-3 w-3" /> Explicación
                          </div>
                          <p className="text-sm text-white/80 leading-relaxed">{q.explanation}</p>
                        </div>
                        <div className="mt-5 flex justify-end">
                          <button
                            onClick={next}
                            data-testid="demo-next"
                            className="group inline-flex items-center gap-2 px-5 h-11 rounded-xl bg-gcgold hover:bg-gcgold-light text-ink font-bold text-sm transition-colors"
                          >
                            {idx === DEMO_QUESTIONS.length - 1 ? "Ver resultado" : "Siguiente"}
                            <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              </AnimatePresence>
            ) : (
              /* RESULT SCREEN */
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-8"
                data-testid="demo-result"
              >
                <div className="inline-flex items-center justify-center h-20 w-20 rounded-2xl bg-gcgold/20 mb-5">
                  <span className="font-display text-4xl font-extrabold text-gcgold tabular-nums">
                    {correctCount}/{DEMO_QUESTIONS.length}
                  </span>
                </div>
                <h3 className="font-display text-3xl font-extrabold text-white tracking-tighter">
                  {correctCount === DEMO_QUESTIONS.length ? "¡Perfecto! 🏆" : correctCount >= 2 ? "¡Vas por buen camino!" : "Hay margen de mejora"}
                </h3>
                <p className="mt-3 text-white/60 max-w-md mx-auto">
                  Esto es solo el aperitivo. Con la plataforma completa tienes <strong className="text-white">+13.500 preguntas</strong>, simulacros oficiales y ranking en vivo.
                </p>

                {/* Locked preview of what's behind */}
                <div className="mt-7 grid grid-cols-3 gap-2 max-w-md mx-auto">
                  {["Simulacros", "Estadísticas", "Ranking"].map((f) => (
                    <div key={f} className="relative rounded-lg bg-white/[0.03] border border-white/5 p-3 overflow-hidden">
                      <Lock className="h-3.5 w-3.5 text-gcgold/60 mx-auto mb-1" />
                      <div className="text-[10px] font-mono uppercase tracking-wider text-white/40">{f}</div>
                    </div>
                  ))}
                </div>

                <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center items-center">
                  <Link
                    to="/premium"
                    data-testid="demo-cta-premium"
                    className="group inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-gcgold hover:bg-gcgold-light text-ink font-bold transition-colors shadow-[0_10px_40px_-10px_rgba(212,175,55,0.55)]"
                  >
                    Sigue practicando — créate cuenta
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                  <button
                    onClick={restart}
                    className="text-sm font-medium text-white/60 hover:text-white underline decoration-gcgold/30 decoration-2 underline-offset-4 hover:decoration-gcgold transition-colors"
                    data-testid="demo-restart"
                  >
                    Reiniciar demo
                  </button>
                </div>
              </motion.div>
            )}
          </div>
        </div>

        {/* Bottom glow */}
        <div className="absolute -bottom-10 left-10 right-10 h-20 bg-gcgold/20 blur-3xl opacity-50 -z-10" />
      </motion.div>
    </section>
  );
}
